<template>
  <Breadcrumb></Breadcrumb>
  <div class="px-4 sm:px-0" v-if="physicalPerson">
    <div class="grid grid-cols-3">
      <div class="col-span-2">
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow m-4">
          <div class="px-4 py-5 sm:p-6">
            <div class="flex gap-2">
              <div class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                <UserIcon class="h-8 w-8 text-gray-700"></UserIcon>
              </div>
              <div class="p-2">
                <p class="text-m font-semibold">{{ physicalPerson.attributes.name }}</p>
              </div>
              <div class="p-2">
                <StatusLabel v-if="riskScoring" :riskScoring="riskScoring" />
              </div>
              <div class="p-2">
                <RiskScoringLabel v-if="riskScoring" :level="riskScoring.attributes.scoreLevel ?? undefined" />
              </div>
              <router-link class="p-2 text-indigo-600 hover:text-indigo-900 ml-auto transition-transform duration-200 hover:scale-110" :to="{ name: 'edit-physical-person', params: { id: physicalPerson.id } }">
                <PencilSquareIcon class="size-5" aria-hidden="true" />
              </router-link>
            </div>
          </div>
        </div>
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow m-4">
            <RiskScoringFactorList v-if="riskScoring" :riskScoring="riskScoring" :riskScoringStore="riskScoringStore" @updated="handleRiskScoringUpdate"/>
        </div>
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow m-4">
          <div class="px-4 py-5 sm:px-6">
            <SaleListingAgreementList :saleListingAgreements="physicalPerson.relationships?.saleListingAgreements.data"></SaleListingAgreementList>
          </div>
          <div class="px-4 py-5 sm:px-6">
            <RentalListingAgreementList :rentalListingAgreements="physicalPerson.relationships?.rentalListingAgreements.data"></RentalListingAgreementList>
          </div>
        </div>
      </div>
      <div class="col-span-1">
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1 m-4">
          <div class="px-4 py-5 sm:px-6">
            <span class="text-xl font-semibold text-gray-900">{{ $gettext("Aperçu") }}</span>
          </div>
          <RiskScoringChart v-if="riskScoring" :riskScoring="riskScoring" />
        </div>
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1 m-4">
          <div class="px-4 py-5 sm:px-6" v-if="riskScoring">
            <SanctionsBox v-if="riskScoring" :riskScoring="riskScoring" />
          </div>
        </div>
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1 m-4">
          <div class="px-4 py-5 sm:px-6" v-if="physicalPerson.relationships.physicalPersonDocuments.data">
            <DocumentList v-if="physicalPerson.relationships.physicalPersonDocuments.data" :documents="physicalPerson.relationships.physicalPersonDocuments.data" />
          </div>
        </div>
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1 m-4">
          <div class="px-4 py-5 sm:px-6">
            <ReportList :reports="physicalPersonReportStore.all" report-type="physicalPerson" @build="onBuildReport" @delete="onDeleteReport"></ReportList>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from "vue";
import { ref } from "vue";
import { usePhysicalPersonStore } from "@/stores/physicalPerson.js";
import { useRiskScoring } from "@/stores/riskScoring.js";
import { useRoute } from "vue-router";
import RiskScoringLabel from "@/components/risk-scorings/RiskScoringLabel.vue";
import StatusLabel from "@/components/layout/StatusLabel.vue";
import RiskScoringFactorList from "@/components/risk-scorings/RiskScoringFactorList.vue";
import { UserIcon } from "@heroicons/vue/24/outline";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart } from "echarts/charts";
import { PencilSquareIcon } from "@heroicons/vue/24/solid";
import DocumentList from "@/components/documents/DocumentList.vue";

import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from "echarts/components";
import Breadcrumb from "@/components/layout/BreadcrumbNavigation.vue";
import RiskScoringChart from "@/components/risk-scorings/RiskScoringChart.vue";
import SanctionsBox from "@/components/risk-scorings/SanctionsBox.vue";
import SaleListingAgreementList from "@/components/sale-listing-agreements/SaleListingAgreementList.vue";
import RentalListingAgreementList from "@/components/rental-listing-agreements/RentalListingAgreementList.vue";
import ReportList from "@/components/reports/ReportList.vue";
import { usePhysicalPersonReportStore } from "@/stores/physicalPersonReport";

use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent
]);

const route = useRoute();
const physicalPersonId = route.params.id;
const physicalPersonStore = usePhysicalPersonStore();
const riskScoringStore = useRiskScoring("physical-persons", physicalPersonId);
const physicalPersonReportStore = usePhysicalPersonReportStore(physicalPersonId);
const physicalPerson = ref(null);
const riskScoring = ref(null);


const onBuildReport = async () => {
  await physicalPersonStore.buildReport(physicalPersonId, () => {
    physicalPersonReportStore.fetchAll();
  }, null, physicalPersonIncludes);
};

const onDeleteReport = async (report) => {
  await physicalPersonStore.deleteReport(report.id, physicalPersonId, () => {
    physicalPersonReportStore.fetchAll();
  });
};

const handleRiskScoringUpdate = async () => {
  await loadPhysicalPerson();
  let riskScoringId = physicalPerson.value.relationships.lastRiskScoring.data.id
  riskScoring.value = await riskScoringStore.fetchOne(riskScoringId);
};

const physicalPersonIncludes = ["saleListingAgreements", "rentalListingAgreements", "physicalPersonDocuments", "lastRiskScoring"];

const loadPhysicalPerson = async () => {
  physicalPerson.value = await physicalPersonStore.fetchPhysicalPerson(physicalPersonId, null, physicalPersonIncludes);
};

onMounted(async () => {
  await loadPhysicalPerson();
  let riskScoringId = physicalPerson.value.relationships.lastRiskScoring.data.id
  riskScoring.value = await riskScoringStore.fetchOne(riskScoringId);
  await physicalPersonReportStore.fetchAll();
  await physicalPersonReportStore.watchResourceChanges(() => {
    physicalPersonReportStore.fetchAll();
  });
});

</script>
